import React, { FC, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { Box } from '@mui/material';
import { useTypedSelector } from '../../../store/store';
import { selectUploadPhase, UploadPhase } from '../../../services/upload/uploadPhase';
import { BODY_FONT, g, t, TICKET } from '../HomeScreen/HomeScreen.styles';
import { TicketPage, TicketPaper, TicketPerforation } from '../Ticket/Ticket';
import { SupportPill } from '../SupportPill/SupportPill';
import { ProgressRing } from './components/ProgressRing';
import { MeterLine } from './components/MeterLine';
import { UPLOAD_COPY } from './uploadCopy';

/**
 * The upload flow on the video ticket: upload status at the top, the
 * current step (identity, questions, "just a moment") in the middle, live
 * chat at the foot. Used by the upload carousel and by the identity form
 * that SignUpScreen lays over it while the upload starts.
 */

/** Bytes are up and the session is closed: same test the carousel latches. */
export const useUploadDone = (): boolean =>
  useTypedSelector(({ upload }) => {
    return (
      !upload.isUploadInprogress &&
      upload.uploadingProgress === 100 &&
      !upload.isPausedForWifi &&
      !upload.isUploadError &&
      !upload.isUploadFatal &&
      !upload.isResumingUpload
    );
  });

const StatusSection = styled(Box)({
  padding: `${g(36)} ${g(40)} ${g(26)}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: g(14),
  fontFamily: BODY_FONT,
  textAlign: 'center',
});

const StatusTitle = styled('p')({
  margin: 0,
  minHeight: t(26),
  fontFamily: BODY_FONT,
  fontSize: t(22),
  lineHeight: t(26),
  fontWeight: 400,
  color: '#4c5257',
});

const Notice = styled(Box, { shouldForwardProp: (p) => p !== '$alert' })<{ $alert: boolean }>(({ $alert }) => ({
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  gap: g(16),
  padding: `${g(14)} ${g(18)}`,
  borderRadius: g(10),
  background: $alert ? '#fbe4e2' : '#eceef1',
  color: '#4c5257',
  fontSize: t(15.5),
  lineHeight: 1.35,
  textAlign: 'left',
}));

const NoticeIcon = styled('span', { shouldForwardProp: (p) => p !== '$alert' })<{ $alert: boolean }>(({ $alert }) => ({
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  background: $alert ? '#d04949' : '#5b9bd5',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 700,
  fontFamily: 'Georgia, serif',
}));

const pillIn = keyframes`
  from { opacity: 0; transform: translateY(-6px) scale(.98); }
  to { opacity: 1; transform: none; }
`;

const CompletePill = styled(Box)(({ theme }) => ({
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: g(16),
  minHeight: `max(52px, ${g(104)})`,
  padding: `0 ${g(20)}`,
  borderRadius: g(16),
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  boxShadow: '0 1.5px 2px rgba(0,0,0,.24), 0 8px 18px rgba(0,0,0,.08)',
  fontFamily: BODY_FONT,
  fontSize: t(30),
  fontWeight: 700,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  animation: `${pillIn} 420ms cubic-bezier(.16,1,.3,1) both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

const Thanks = styled('h2')({
  margin: `${g(8)} 0 0`,
  fontFamily: BODY_FONT,
  fontSize: t(34),
  lineHeight: 1.15,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: TICKET.labelInk,
  '& span': { display: 'block' },
});

interface StatusProps {
  /** Upload finished: show "Upload Complete!" instead of the ring. */
  complete: boolean;
  /** Questions answered too: the thank-you under it. */
  thanks?: boolean;
}

/** Top section of the upload ticket. */
export const UploadStatus: FC<StatusProps> = ({ complete, thanks }) => {
  const phase = useTypedSelector(selectUploadPhase);
  const isPausedForWifi = useTypedSelector((state) => state.upload.isPausedForWifi);
  const copy = UPLOAD_COPY[phase];
  // Interruptions, Wi-Fi pause and failure explain themselves in a notice
  // in place of the title.
  const notice = phase !== UploadPhase.Complete ? copy.banner : undefined;

  if (complete && !isPausedForWifi) {
    return (
      <StatusSection>
        <CompletePill role="status">
          <span aria-hidden="true">&#127881;</span>
          <span>Upload Complete!</span>
        </CompletePill>
        {thanks && (
          <Thanks>
            <span>Thanks for sharing</span>
            <span>your story</span>
          </Thanks>
        )}
      </StatusSection>
    );
  }

  return (
    <StatusSection>
      {notice ? (
        <Notice $alert={!notice.informational} role="status">
          <NoticeIcon $alert={!notice.informational} aria-hidden="true">
            i
          </NoticeIcon>
          <span>{notice.text}</span>
        </Notice>
      ) : (
        <StatusTitle>{copy.title || 'Preparing Video for upload'}</StatusTitle>
      )}
      <ProgressRing />
      <MeterLine />
    </StatusSection>
  );
};

const StepSection = styled(Box)({
  flex: 1,
  minHeight: `max(230px, ${g(400)})`,
  padding: `${g(26)} ${g(36)} ${g(22)}`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  fontFamily: BODY_FONT,
});

const FootSection = styled(Box)({
  padding: `${g(24)} ${g(40)} ${g(38)}`,
  display: 'flex',
  justifyContent: 'center',
});

interface TicketProps {
  status: ReactNode;
  /** The current step. Omitted on the complete ticket, which then has a
   *  single perforation, above the foot. */
  children?: ReactNode;
}

export const UploadTicket: FC<TicketProps> = ({ status, children }) => (
  <TicketPage fill>
    <TicketPaper fill>
      {status}
      {children !== undefined && <TicketPerforation />}
      <StepSection>{children}</StepSection>
      <TicketPerforation />
      <FootSection>
        <SupportPill />
      </FootSection>
    </TicketPaper>
  </TicketPage>
);
